import axios from 'axios';
import { createEncryptedAxios } from './cryptoAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Debug: Log environment configuration on module load
console.log('🔧 Frontend API Configuration:');
console.log('  REACT_APP_API_URL:', process.env.REACT_APP_API_URL || '(not set, using default: /api)');
console.log('  REACT_APP_API_KEY:', process.env.REACT_APP_API_KEY ? `${process.env.REACT_APP_API_KEY.substring(0, 10)}...` : '(not set)');
console.log('  Final API_BASE_URL:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Apply encrypted authentication to axios instance
createEncryptedAxios(api);

// Consultation API

// Fetches the signed token that proves the enrollment form was actually
// rendered. The backend embeds a signed issue timestamp in it to enforce a
// minimum fill time, so this must be requested when the form mounts - not at
// submit time, or the submission will be rejected as too fast.
export const getConsultationFormToken = async () => {
  try {
    const response = await api.get('/consultation/form-token');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const bookConsultation = async (consultationData) => {
  try {
    const response = await api.post('/consultation', consultationData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Transformations API
export const getTransformations = async (params = {}) => {
  try {
    const response = await api.get('/transformations', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const getTransformation = async (id) => {
  try {
    const response = await api.get(`/transformations/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Recipes API
export const getRecipes = async (params = {}) => {
  try {
    const response = await api.get('/recipes', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const getRecipe = async (id) => {
  try {
    const response = await api.get(`/recipes/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Contact API
export const submitContact = async (contactData) => {
  try {
    const response = await api.post('/contact', contactData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Config API
export const getConfig = async () => {
  try {
    const response = await api.get('/config');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Blog API (public endpoints)
export const getPublicBlogs = async (params = {}) => {
  try {
    const response = await api.get('/blogs', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const getPublicBlog = async (id) => {
  try {
    const response = await api.get(`/blogs/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const getBlogCategories = async () => {
  try {
    const response = await api.get('/blog-categories');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

export const likeBlog = async (id) => {
  try {
    const response = await api.post(`/blogs/${id}/like`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Reviews API (public)
export const getReviews = async (params = {}) => {
  try {
    const response = await api.get('/reviews', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Network error' };
  }
};

// Presigned URL API (for private S3 objects)
export const getPresignedUrl = async (s3Key, expiration = 3600) => {
  try {
    const response = await api.get('/presigned-url', { 
      params: { key: s3Key, expiration } 
    });
    return response.data.presigned_url;
  } catch (error) {
    console.error('Failed to get presigned URL:', error);
    throw error.response?.data || { error: 'Network error' };
  }
};

export default api;
